import React, { useEffect, useRef, useContext } from "react";
import { LMDrawingContext, ThemeContext } from "../contexts/AppContext";
import { getAllMeasurementsForLMInfo, getImpactMeasurementKeys, getImpactTargetHalfMm } from "../utils/Utilities";

const INCH_TO_MM = 25.4;
const CANVAS_SIZE_PX = 260;
const CANVAS_RANGE_MM = 5;
const LINE_TOLERANCE_MM = 0.05;

function ImpactTargetBox({ selectedSetup, selectedShot, robotFaceMap }) {
  const canvasRef = useRef(null);
  const { lmDrawing } = useContext(LMDrawingContext);
  const { theme } = useContext(ThemeContext);
  const checkedVersion =
    selectedSetup?.Shots?.map((shot) => (shot?.IsChecked ? 1 : 0)).join("") ?? "";

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const size = canvas.width;
    const center = size / 2;
    const mmToPx = size / CANVAS_RANGE_MM;

    const rootStyles = getComputedStyle(document.documentElement);
    //const bg = rootStyles.getPropertyValue("--background-secondary").trim() || "#000000";
    const bg = rootStyles.getPropertyValue("--background-primary").trim() || "#000000";
    
    const fg = rootStyles.getPropertyValue("--foreground-primary").trim() || "#ffffff";
    const fgMuted = rootStyles.getPropertyValue("--foreground-secondary").trim() || "#cccccc";
    const border = rootStyles.getPropertyValue("--background-tertiary").trim() || "#000000";
    const invert = rootStyles.getPropertyValue("--background-primary-invert").trim() || "#000000";

    const targetInfo = selectedSetup?.TestParameters?.RobotFaceMap || {};//robotFaceMap;
    const targetXInch = Number(targetInfo?.y);
    const targetYInch = Number(targetInfo?.z);
    const hasTarget = Number.isFinite(targetXInch) && Number.isFinite(targetYInch);
    const targetX = (hasTarget ? targetXInch : 0) * INCH_TO_MM;
    const targetY = (hasTarget ? targetYInch : 0) * INCH_TO_MM;
    const safeHalfMm = getImpactTargetHalfMm(targetInfo,robotFaceMap);
    const { xKey, yKey, isAdjusted } = getImpactMeasurementKeys(targetInfo,robotFaceMap);

    let selectedOutside = false;

    if (selectedShot) {
      const lmInfo = Array.isArray(selectedShot?.LMInfos)
        ? selectedShot.LMInfos.find((info) => info?.LMType === lmDrawing)
        : null;

      const measurements = getAllMeasurementsForLMInfo(lmInfo);
      const xInch = Number(measurements[xKey]);
      const yInch = Number(measurements[yKey]);

      if (Number.isFinite(xInch) && Number.isFinite(yInch)) {
        const xMm = xInch * INCH_TO_MM;
        const yMm = yInch * INCH_TO_MM;
        const dx = xMm - targetX;
        const dy = yMm - targetY;
        selectedOutside = Math.abs(dx) > safeHalfMm || Math.abs(dy) > safeHalfMm;
      }
    }

    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, size, size);

    ctx.lineWidth = 1;
    ctx.strokeStyle = selectedOutside ? "#e53935" : border;
    ctx.strokeRect(0.5, 0.5, size - 1, size - 1);

    const safeHalfPx = safeHalfMm * mmToPx;
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = fgMuted;
    ctx.strokeRect(center - safeHalfPx, center - safeHalfPx, safeHalfPx * 2, safeHalfPx * 2);
    ctx.setLineDash([]);

    ctx.lineWidth = 1;
    ctx.strokeStyle = fg;
    ctx.beginPath();
    ctx.moveTo(center - 8, center);
    ctx.lineTo(center + 8, center);
    ctx.moveTo(center, center - 8);
    ctx.lineTo(center, center + 8);
    ctx.stroke();

    //const shots = Array.isArray(selectedSetup?.Shots) ? selectedSetup.Shots : []; //plots all
    const shots = Array.isArray(selectedSetup?.Shots) ? selectedSetup.Shots.filter((shot) => shot?.IsChecked): [];//plots just selected
    const points = [];

    shots.forEach((shot) => {
      const lmInfo = Array.isArray(shot?.LMInfos)
        ? shot.LMInfos.find((info) => info?.LMType === lmDrawing)
        : null;

      if (!lmInfo) return;

      const measurements = getAllMeasurementsForLMInfo(lmInfo);
      const xInch = Number(measurements[xKey]);
      const yInch = Number(measurements[yKey]);

      if (!Number.isFinite(xInch) || !Number.isFinite(yInch)) return;

      points.push({
        shotNumber: shot.ShotNumber,
        xMm: xInch * INCH_TO_MM,
        yMm: yInch * INCH_TO_MM
      });
    });

    if (!hasTarget) {
      ctx.fillStyle = fg;
      ctx.font = "bold 20px Arial";
      ctx.textAlign = "center";
      ctx.fillText("No face map target", center, center + 60);
    }

    if (points.length === 0) {
      ctx.fillStyle = fgMuted;
     // ctx.font = "bold 14px 'Comfortaa'";
       ctx.font = "bold 20px Arial";
      ctx.textAlign = "center";
      ctx.fillText("No impact locations", center, center + 105);
      return;
    }

    points.forEach((point) => {
      const dx = point.xMm - targetX;
      const dy = point.yMm - targetY;
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);

      const onLine =
        (Math.abs(absX - safeHalfMm) <= LINE_TOLERANCE_MM && absY <= safeHalfMm + LINE_TOLERANCE_MM) ||
        (Math.abs(absY - safeHalfMm) <= LINE_TOLERANCE_MM && absX <= safeHalfMm + LINE_TOLERANCE_MM);

      const inside =
        absX < safeHalfMm - LINE_TOLERANCE_MM &&
        absY < safeHalfMm - LINE_TOLERANCE_MM;

      let color = "#e53935";
      if (onLine) {
        color = "#f4d03f";
      } else if (inside) {
        color = "#2ecc71";
      }

      const xPx = center - dx * mmToPx;////TRYING THIS FLIPPED!!
      const yPx = center - dy * mmToPx;

      ctx.beginPath();
      ctx.arc(xPx, yPx, 4.5, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.lineWidth = 1;
      ctx.strokeStyle = invert;
      ctx.stroke();

      if (selectedShot && point.shotNumber === selectedShot.ShotNumber) {
        ctx.beginPath();
        ctx.arc(xPx, yPx, 8, 0, Math.PI * 2);
        ctx.lineWidth = 2;
        ctx.strokeStyle = fg;
        ctx.stroke();
      }
    });

    if (isAdjusted) {
      ctx.textAlign = "right";
      ctx.fillStyle = "#ffffffcc";
      ctx.font = "italic 14px Arial";
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.strokeText("Adj", size - 8, size - 8);
      ctx.fillText("Adj", size - 8, size - 8);
    }
    else{
      ctx.textAlign = "right";
      ctx.fillStyle = "#ffffffcc";
      ctx.font = "italic 14px Arial";
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.strokeText("Raw", size - 8, size - 8);
      ctx.fillText("Raw", size - 8, size - 8);

    }
  }, [selectedSetup, selectedShot, robotFaceMap, lmDrawing, theme, checkedVersion]);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_SIZE_PX}
      height={CANVAS_SIZE_PX}
      style={{ display: "block", margin: "12px auto" }}
    />
  );
}

export default ImpactTargetBox;
